import { showToast } from '@bp/ui-components';
import { Form, Formik } from 'formik';
import { DatePicker, Grid, GridColumn, GridRow, Input, Select } from '@bp/ui-components';
import { useTranslation } from 'react-i18next';
import { validationSchema } from './nextcloudForm.schema';
import { ensureDate } from '../../../utils/dateCalculations';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { OrganizationConfigContext } from '../../../context/OrganizationConfigContext';
import { useContext } from 'react';
import { graphql } from 'client/gql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useMutation, useQuery } from 'urql';
import { useSuspenseQuery } from '@tanstack/react-query';
import { backendApi } from 'utils/backendApi';

const CreateNextcloudsMutationDocument = graphql(`
  mutation CreateNextclouds($input: [NextcloudCreateInput!]!) {
    createNextclouds(input: $input) {
      nextclouds {
        uuid
        __typename
        serverUri
        organizationConnection {
          __typename
          edges {
            __typename
            properties {
              __typename
              notAfter
              notBefore
            }
          }
        }
      }
    }
  }
`);

const UpdateNextcloudsMutationDocument = graphql(`
  mutation UpdateNextclouds($update: NextcloudUpdateInput!, $where: NextcloudWhere!) {
    updateNextclouds(update: $update, where: $where) {
      nextclouds {
        uuid
        __typename
        serverUri
        organizationConnection {
          __typename
          edges {
            __typename
            properties {
              __typename
              notAfter
              notBefore
            }
          }
        }
      }
    }
  }
`);

export const SettingsNextcloudFormQueryDocument = graphql(`
  query SettingsNextcloudFormData($profile: ID!, $organizationUuid: ID!) {
    nextclouds(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      serverUri
      serverAliase
      organizationConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            notBefore
            notAfter
          }
        }
      }
    }
    ...PermissionCheckerFields
  }
`);

type NextcloudValuesType = {
  adminPass: string;
  adminUser: string;
  serverUri: string;
  serverAliase?: Array<string> | null;
  notBefore: string;
  notAfter: string;
};

type NextcloudFormProps = {
  onClose: () => void;
};

export const NextcloudForm = ({ onClose }: NextcloudFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const { currentSchoolYear } = useContext(OrganizationConfigContext);

  const [{ data: queryData }] = useQuery({
    query: SettingsNextcloudFormQueryDocument,
    variables: {
      organizationUuid: organizationUuid,
      profile: profileUuid,
    },
  });

  const permissions = usePermissionChecker(queryData);

  const currentBooking = queryData?.nextclouds[0];

  const serverUriAlreadySet = Boolean(currentBooking?.serverUri);

  const { data: ncClients } = useSuspenseQuery<
    Array<{
      __typename?: 'AuthClient';
      clientId: string;
      clientName?: string | null;
      redirectUris?: Array<string> | null;
      postLogoutRedirectUris?: Array<string> | null;
      responseTypes: Array<string>;
      grantTypes: Array<string>;
      scope?: string | null;
      tokenEndpointAuthMethod: string;
    }>
  >({
    queryKey: ['nextcloudClients'],
    queryFn: async () => {
      const response = await backendApi.fetch(`/setup/cloud`);
      return await response.json();
    },
  });
  const ncClientSelectOptions = (ncClients ?? []).map((c) => ({
    label: c.clientName ?? '',
    value: c.clientId,
  }));

  const [, updateNextclouds] = useMutation(UpdateNextcloudsMutationDocument);
  const [, createNextclouds] = useMutation(CreateNextcloudsMutationDocument);

  const getClientNameByClientId = (clientId: string | null | undefined) =>
    ncClients.find((c) => c.clientId === clientId)?.clientName;

  const initialValues: NextcloudValuesType = {
    adminPass: '',
    adminUser: 'admin.bp',
    serverUri: currentBooking?.serverUri ?? ncClientSelectOptions?.[0].value,
    serverAliase: currentBooking?.serverAliase ?? [],
    notBefore: ensureDate(
      currentBooking?.organizationConnection.edges[0]?.properties.notBefore ?? currentSchoolYear.start,
    ).toUTCString(),
    notAfter: ensureDate(
      currentBooking?.organizationConnection.edges[0]?.properties.notAfter ?? currentSchoolYear.end,
    ).toUTCString(),
  };

  const onSubmit = async (values: NextcloudValuesType) => {
    const serverUri = serverUriAlreadySet ? values.serverUri : getClientNameByClientId(values.serverUri);
    if (!serverUri) return showToast(t('settings.nextcloud.serverUriMissing'), { type: 'error' });

    if (permissions?.canUpdateNextcloud({ uuid: organizationUuid })) {
      if (!currentBooking) {
        const resp = await createNextclouds({
          input: [
            {
              serverUri,
              adminUser: values.adminUser,
              adminPass: values.adminPass,
              organization: {
                connect: {
                  overwrite: true,
                  edge: {
                    notAfter: values.notAfter,
                    notBefore: values.notBefore,
                  },
                  where: { node: { uuid: organizationUuid } },
                },
              },
            },
          ],
        });

        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          showSuccessToast(t('common.saved'));
          onClose();
        }
      } else {
        const resp = await updateNextclouds({
          update: {
            serverUri,
            organization: {
              update: {
                edge: {
                  notAfter: values.notAfter,
                  notBefore: values.notBefore,
                },
              },
            },
          },
          where: { uuid: currentBooking.uuid },
        });

        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          showSuccessToast(t('common.saved'));
          onClose();
        }
      }
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ resetForm, setFieldValue, handleChange, handleBlur, values, isSubmitting, errors }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingBottom='s'>
                <GridColumn width={6}>
                  <DatePicker
                    label={t('institution.bookedFromDate')}
                    onChange={(e) => setFieldValue('notBefore', e?.toUTCString())}
                    value={ensureDate(values.notBefore)}
                    name={'notBefore'}
                    showMonthYearDropdown
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <DatePicker
                    label={t('institution.bookedUntilDate')}
                    onChange={(e) => setFieldValue('notAfter', e?.toUTCString())}
                    value={ensureDate(values.notAfter)}
                    name={'notAfter'}
                    showMonthYearDropdown
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingBottom='s' spacingTop='s'>
                {serverUriAlreadySet ? (
                  <Input
                    required
                    disabled
                    label={t('settings.nextcloudServerUri')}
                    name={'serverUri'}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.serverUri}
                    error={errors.serverUri}
                  />
                ) : (
                  <Select
                    label={t('settings.nextcloudServerUri')}
                    name={'serverUri'}
                    onBlur={handleBlur}
                    onChange={(value) => setFieldValue('serverUri', (value as { value: string; label: string }).value)}
                    error={errors.serverUri}
                    options={ncClientSelectOptions}
                    defaultValue={ncClientSelectOptions?.[0]}
                  />
                )}
              </GridRow>
              {!serverUriAlreadySet && (
                <>
                  <GridRow spacingBottom='s' spacingTop='s'>
                    <Input
                      required
                      label={t('settings.nextcloudAdminUser')}
                      name={'adminUser'}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.adminUser}
                      error={errors.adminUser}
                    />
                  </GridRow>
                  <GridRow spacingTop='s'>
                    <Input
                      required
                      label={t('settings.nextcloudAdminPass')}
                      name={'adminPass'}
                      type='password'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.adminPass}
                      error={errors.adminPass}
                    />
                  </GridRow>
                </>
              )}
            </Grid>

            <ModalBottomButtons
              errors={errors}
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              isLoading={isSubmitting}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
